import TInterviewThread, {
    TInterviewGoalStatus,
    TInterviewMessage,
    TInterviewThreadStatus,
} from "./TInterviewThread";
import {TInterviewThreadApiError} from "./InterviewThreadApiError";

export type TProcessingRetryMode = "resume"|"verify"|"requeue";

export type TConversationState =
    | {type: "loading"}
    | {type: "loadFailed", error: TInterviewThreadApiError}
    | {type: "waitingForUser"}
    | {type: "inputRejected", error: TInterviewThreadApiError}
    | {type: "submitting"}
    | {type: "sendFailed", error: TInterviewThreadApiError, clientMessageId: string}
    | {type: "processing", jobId: number, failedSince: number|null}
    | {
        type: "processingFailed",
        jobId: number,
        error: TInterviewThreadApiError,
        retryMode: TProcessingRetryMode,
    }
    | {type: "reconciliationRequired", error: TInterviewThreadApiError}
    | {type: "completed"}
    | {type: "closed", status: "cancelled"|"failed"};

export type TInterviewThreadStoreState = {
    conversation: TConversationState,
    threadStatus: TInterviewThreadStatus|null,
    startedAt: string|null,
    completedAt: string|null,
    closedAt: string|null,
    messages: TInterviewMessage[],
    goals: TInterviewGoalStatus[],
    input: string,
};

export type TInterviewThreadStoreEvent =
    | {type: "loadStarted"}
    | {
        type: "threadLoaded",
        thread: TInterviewThread,
        missingJobError: TInterviewThreadApiError,
    }
    | {type: "loadFailed", error: TInterviewThreadApiError}
    | {type: "inputChanged", input: string}
    | {type: "submitStarted"}
    | {type: "submitSucceeded", message: TInterviewMessage, jobId: number}
    | {type: "submitRejected", error: TInterviewThreadApiError}
    | {type: "sendFailed", error: TInterviewThreadApiError, clientMessageId: string}
    | {type: "processingStatus", jobId: number, failedSince: number|null}
    | {
        type: "processingFailed",
        jobId: number,
        error: TInterviewThreadApiError,
        retryMode: TProcessingRetryMode,
    };

export const MAX_MESSAGE_LENGTH = 10000;

export function reduceInterviewThreadStore(
    state: TInterviewThreadStoreState,
    event: TInterviewThreadStoreEvent,
): TInterviewThreadStoreState {
    switch (event.type) {
        case "loadStarted":
            return {...state, conversation: {type: "loading"}};

        case "threadLoaded": {
            let conversation: TConversationState;
            switch (event.thread.status) {
                case "waiting_for_user":
                    conversation = {type: "waitingForUser"};
                    break;
                case "waiting_for_agent":
                    conversation = event.thread.activeProcessingJob !== null
                        ? {
                            type: "processing",
                            jobId: event.thread.activeProcessingJob.id,
                            failedSince: null,
                        }
                        : {
                            type: "reconciliationRequired",
                            error: event.missingJobError,
                        };
                    break;
                case "completed":
                    conversation = {type: "completed"};
                    break;
                case "cancelled":
                case "failed":
                    conversation = {type: "closed", status: event.thread.status};
                    break;
            }

            return {
                ...state,
                conversation,
                threadStatus: event.thread.status,
                startedAt: event.thread.startedAt ?? null,
                completedAt: event.thread.completedAt ?? null,
                closedAt: event.thread.closedAt ?? null,
                messages: event.thread.messages,
                goals: event.thread.goals,
            };
        }

        case "loadFailed":
            return {...state, conversation: {type: "loadFailed", error: event.error}};

        case "inputChanged":
            return {
                ...state,
                input: event.input,
                conversation: state.conversation.type === "inputRejected"
                    ? {type: "waitingForUser"}
                    : state.conversation,
            };

        case "submitStarted":
            if (state.conversation.type !== "waitingForUser"
                && state.conversation.type !== "inputRejected"
                && state.conversation.type !== "sendFailed"
            ) {
                return state;
            }
            return {...state, conversation: {type: "submitting"}};

        case "submitSucceeded":
            return {
                ...state,
                input: "",
                messages: state.messages.some(message => message.id === event.message.id)
                    ? state.messages
                    : [...state.messages, event.message],
                conversation: {
                    type: "processing",
                    jobId: event.jobId,
                    failedSince: null,
                },
            };

        case "submitRejected":
            return {...state, conversation: {type: "inputRejected", error: event.error}};

        case "sendFailed":
            return {...state, conversation: {
                type: "sendFailed",
                error: event.error,
                clientMessageId: event.clientMessageId,
            }};

        case "processingStatus":
            return {...state, conversation: {
                type: "processing",
                jobId: event.jobId,
                failedSince: event.failedSince,
            }};

        case "processingFailed":
            return {...state, conversation: {
                type: "processingFailed",
                jobId: event.jobId,
                error: event.error,
                retryMode: event.retryMode,
            }};
    }
}

export default class InterviewThreadStore {

    private state: TInterviewThreadStoreState = {
        conversation: {type: "loading"},
        threadStatus: null,
        startedAt: null,
        completedAt: null,
        closedAt: null,
        messages: [],
        goals: [],
        input: "",
    };

    constructor(
        private readonly readonly: boolean,
    ) {
    }

    public dispatch(event: TInterviewThreadStoreEvent): void {
        this.state = reduceInterviewThreadStore(this.state, event);
    }

    public getState(): Readonly<TInterviewThreadStoreState> {
        return this.state;
    }

    public isInputTooLong(): boolean {
        return Array.from(this.state.input).length > MAX_MESSAGE_LENGTH;
    }

    public isInputDisabled(): boolean {
        return this.readonly
            || (this.state.conversation.type !== "waitingForUser"
                && this.state.conversation.type !== "inputRejected");
    }

    public canSend(): boolean {
        const length = Array.from(this.state.input.trim()).length;

        return !this.isInputDisabled()
            && length > 0
            && length <= MAX_MESSAGE_LENGTH;
    }

    public canShowSuggestedResponses(): boolean {
        return !this.readonly && this.state.conversation.type === "waitingForUser";
    }

    public getError(): TInterviewThreadApiError|null {
        switch (this.state.conversation.type) {
            case "loadFailed":
            case "inputRejected":
            case "processingFailed":
            case "sendFailed":
            case "reconciliationRequired":
                return this.state.conversation.error;
            default:
                return null;
        }
    }

}
